import { Cookies } from 'quasar'

const CUSTOMER_DISPLAY_COOKIE = 'customerDisplayPosition'

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  if (!app.config.globalProperties.$register) return

  const customerDisplayHandler = {}

  customerDisplayHandler.id = crypto.getRandomValues(new Uint32Array(1))[0]
  customerDisplayHandler.isRegister = true
  customerDisplayHandler.otherWindow = null
  customerDisplayHandler.otherHandler = null

  customerDisplayHandler.connected = false
  customerDisplayHandler.loading = false

  customerDisplayHandler.persistCustomerDisplay = function (otherWindow, otherHandler) {
    customerDisplayHandler.otherWindow = otherWindow
    customerDisplayHandler.otherHandler = otherHandler
    customerDisplayHandler.connected = true
  }
  customerDisplayHandler.openCustomerDisplay = async function () {
    if (window.electronDisplay) {
      window.electronDisplay.exitFullscreen()
      // Wait until we have finished exiting fullscreen
      await new Promise(resolve => setTimeout(resolve, 500))
    } else if (window.document.fullscreenElement) {
      window.document.exitFullscreen()
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    if (window.electronDisplay) {
      // Disable frame to prevent file/menu bars on Windows Electron installs.
      // Disabling frame prevents operators from moving the window manually.
      // The frame will have to be moved using the screen selection dialog. It will not be able to be moved afterwards.
      customerDisplayHandler.otherWindow = window.open(`${window.location.origin}/customer-display`, 'INDY Customer Display', 'popup=yes,frame=no')
    } else {
      // Include frame on browser so the window can be manually when testing.
      // The screen selection dialog APIs are not available on local in browsers, so this is more necessary.
      customerDisplayHandler.otherWindow = window.open(`${window.location.origin}/customer-display`, 'INDY Customer Display', 'popup')
    }

    if (!!customerDisplayHandler.otherWindow && !customerDisplayHandler.otherWindow?.closed && !!customerDisplayHandler.otherWindow?.$customerDisplayHandler) {
      customerDisplayHandler.persistCustomerDisplay(customerDisplayHandler.otherWindow, customerDisplayHandler.otherWindow.$customerDisplayHandler)
    }
  }

  customerDisplayHandler.closeCustomerDisplay = function () {
    if (customerDisplayHandler.isRegister && !!customerDisplayHandler.otherWindow?.closed) {
      customerDisplayHandler.otherWindow = null
      customerDisplayHandler.otherHandler = null
    } else if (customerDisplayHandler.otherWindow?.closed) {
      window.close()
    } else if (customerDisplayHandler.otherWindow) {
      customerDisplayHandler.connected = false
      customerDisplayHandler.reconnectCustomerDisplay()
    }
  }

  customerDisplayHandler.reconnectCustomerDisplay = function (retryCount = 0) {
    if (customerDisplayHandler.connected) {
      return
    }

    if (retryCount >= 10) return

    // Increase timeout by 2 seconds with each retry - covers up to 90 seconds of loading over 10 tries.
    const timeoutMs = 1000 + (2000 * retryCount)
    setTimeout(() => {
      customerDisplayHandler.otherWindow.postMessage(
        'CONNECT',
        customerDisplayHandler.otherWindow.location.href
      )
      customerDisplayHandler.reconnectCustomerDisplay(retryCount + 1)
    }, timeoutMs)
  }

  customerDisplayHandler.subscribers = {}
  customerDisplayHandler.subscribe = function (feed, name, callback) {
    customerDisplayHandler.subscribers[feed] ||= []
    customerDisplayHandler.subscribers[feed].push({
      name,
      callback
    })
  }
  customerDisplayHandler.unsubscribe = function (feed, name) {
    customerDisplayHandler.subscribers[feed] ||= []
    customerDisplayHandler.subscribers[feed] = _.filter(customerDisplayHandler.subscribers[feed], subscriber => {
      return subscriber.name !== name
    })
  }

  customerDisplayHandler.publications = {}
  customerDisplayHandler.publish = async function (feed, values, stringifyValues = true, parseValues = true) {
    customerDisplayHandler.publications[feed] ||= { json: '' }

    if (customerDisplayHandler.otherHandler) {
      if (customerDisplayHandler.publications[feed].timer) clearTimeout(customerDisplayHandler.publications[feed].timer)

      customerDisplayHandler.publications[feed].timer = setTimeout(customerDisplayHandler.send, 500, feed, values, stringifyValues, parseValues)
    }
  }
  customerDisplayHandler.send = async function (feed, values, stringifyValues = true, parseValues = true) {
    const json = JSON.stringify(stringifyValues ? _.map(values, value => value?.json()) : values)

    // If we have already published or been notified of this value, ignore it to prevent infinite loops
    if (customerDisplayHandler.publications[feed].json === json) return

    customerDisplayHandler.publications[feed].json = json

    customerDisplayHandler.otherHandler.notify(feed, json, parseValues)
  }
  customerDisplayHandler.notify = async function (feed, json, parseValues = true) {
    customerDisplayHandler.publications[feed] ||= { json: '' }

    // If we have already published or been notified of this json, ignore it to prevent infinite loops
    if (customerDisplayHandler.publications[feed].json === json) return

    customerDisplayHandler.publications[feed].json = json
    const values = JSON.parse(json)
    const objects = parseValues ? _.map(values, value => JSON.parse(value)) : values
    _.each(customerDisplayHandler.subscribers[feed], subscriber => {
      subscriber.callback(objects)
    })
  }

  customerDisplayHandler.setCookie = async function (screen) {
    const customerDisplayCookie = Cookies.get(CUSTOMER_DISPLAY_COOKIE)
    await Cookies.set(
      CUSTOMER_DISPLAY_COOKIE,
      {
        screen: screen !== null ? screen : customerDisplayCookie?.screen
      },
      { expires: 365 }
    )
  }
  customerDisplayHandler.getWindowManagementPermission = async function () {
    let state = null

    // The new permission name.
    try {
      ({ state } = await navigator.permissions.query({ name: 'window-management' }))
    } catch (err) {
      if (err.name !== 'TypeError') {
        console.log(`${err.name}: ${err.message}`)
      }
      // The old permission name.
      try {
        ({ state } = await navigator.permissions.query({ name: 'window-placement' }))
      } catch (err) {
        if (err.name === 'TypeError') {
          console.log('Window management not supported.')
        }
        console.log(`${err.name}: ${err.message}`)
      }
    }

    return state
  }
  customerDisplayHandler.selectScreen = async function () {
    const screens = (await window.getScreenDetails()).screens
    await customerDisplayHandler.getWindowManagementPermission()

    let defaultScreen = null
    const customerDisplayCookie = Cookies.get(CUSTOMER_DISPLAY_COOKIE)
    if (customerDisplayCookie) {
      defaultScreen = customerDisplayCookie.screen
    }

    app.config.globalProperties.$mh.showOptions(
      'Select Screen',
      'Select Screen to use for Customer Facing Display',
      _.map(screens, screen => {
        return {
          label: screen.label,
          // For some reason chrome won't pass through the actual screen object - an empty hash is passed instead
          value: `${screen.left},${screen.top}`
        }
      }),
      (screen) => {
        customerDisplayHandler.enterFullscreen(screen)
      },
      true,
      defaultScreen
    )
  }

  customerDisplayHandler.enterFullscreen = function (screen) {
    customerDisplayHandler.setCookie(screen, true)

    window.moveTo(screen.split(',')[0], screen.split(',')[1])
    if (window.opener.electronDisplay) {
      window.opener.electronDisplay.enterFullscreen()
    }
    window.document.body.requestFullscreen()
  }

  customerDisplayHandler.handlerUrl = function () {
    if (customerDisplayHandler.isRegister) {
      return `${window.location.origin}/customer-display`
    } else {
      return window.location.origin
    }
  }

  window.addEventListener('beforeunload', async function () {
    customerDisplayHandler.loading = true
    customerDisplayHandler.otherHandler?.closeCustomerDisplay()
  })
  window.addEventListener('DOMContentLoaded', async function () {
    customerDisplayHandler.loading = false
  })
  window.addEventListener('message', async function (event) {
    // If we receive a message to connect to the other display, make sure it hasn't been closed since sending the message
    if (event.source?.closed && !customerDisplayHandler.isRegister) {
      window.close()
    } else if (event.data === 'CONNECT') {
      // There is a potential for this message to be posted, received, and handled
      // before the other window finishes unloading, so this is only handled after DOM content is loaded.
      if (customerDisplayHandler.loading) return

      customerDisplayHandler.otherWindow = event.source
      customerDisplayHandler.otherHandler = event.source.$customerDisplayHandler
      customerDisplayHandler.otherHandler.persistCustomerDisplay(window, customerDisplayHandler)
    }
  }, false)

  window.$customerDisplayHandler = customerDisplayHandler
  app.config.globalProperties.$customerDisplayHandler = customerDisplayHandler

  if (window.opener) {
    try {
      customerDisplayHandler.isRegister = false
      customerDisplayHandler.otherWindow = window.opener
      customerDisplayHandler.otherHandler = window.opener.$customerDisplayHandler
      customerDisplayHandler.otherHandler.persistCustomerDisplay(window, customerDisplayHandler)
    } catch {
      // This happens if the customer display loads faster than the register - easy fix is to reset the customer display
      location.reload()
    }

    try {
      if (!window.document.fullscreenElement && window.screen.isExtended && 'getScreenDetails' in window) {
        try {
          customerDisplayHandler.selectScreen()
        } catch {
          // Chrome requires the user interaction to use the Multi-screen Window Placement API
          // This isn't required in Electron
          app.config.globalProperties.$mh.showMessage(
            'Select Screen',
            'Allow Customer Facing Display to access your screens?',
            () => {
              customerDisplayHandler.selectScreen()
            }
          )
        }
      }
    } catch (e) {
      console.log(e)
    }
  }
}
